import { useMemo, useEffect } from 'react';
import { useGetRfiTypes } from './queries';
import { Assignee, EntityStatus, RfiStatus, WorkflowType, RfiPriority } from '../sdk';
import { useOxygen } from '../providers/OxygenProvider';
import { incrementTodayByDays } from '../helpers';
import { getFilterValidAssignees } from '../helpers/oxygenHelpers';

export type FieldSetters = {
  assignedTo: (assignees: Assignee[]) => void;
  locationDescription?: (locationDescription: string) => void;
  reference?: (reference: string) => void;
  costImpact?: (costImpact: string) => void;
  scheduleImpact?: (scheduleImpact: string) => void;
  priority?: (priority: string) => void;
  category?: (category: string[]) => void;
  discipline?: (discipline: string[]) => void;
  dueDateOffset?: (date: Date | null) => void;
  watchers?: (assignees: Assignee[]) => void;
};
type usePopulateRfiTypeFieldsArgs = {
  selectedRfiTypeId: string;
  fieldSetters: FieldSetters;
  formStatus: RfiStatus;
  disabled?: boolean;
};
export const usePopulateRfiTypeFields = ({
  selectedRfiTypeId,
  fieldSetters,
  formStatus,
  disabled,
}: usePopulateRfiTypeFieldsArgs) => {
  const oxygen = useOxygen();
  const filterValidAssignees = getFilterValidAssignees(oxygen);

  const { data: rfiTypes, isLoading: isFetchingRfiTypes } = useGetRfiTypes();

  const selectedRfiType = useMemo(() => {
    const activeTypes = rfiTypes?.filter(
      ({ status, id }) => status === EntityStatus.Active || id === selectedRfiTypeId,
    );
    return activeTypes?.find((rfiType) => rfiType.id === selectedRfiTypeId);
  }, [rfiTypes, selectedRfiTypeId]);

  useEffect(() => {
    const doDoNotPopulateFields =
      !selectedRfiType || isFetchingRfiTypes || oxygen.loading || disabled === true;
    if (doDoNotPopulateFields) {
      return;
    }

    const rfiTypeWorkflowMatchFormStatus =
      ([RfiStatus.Submitted, RfiStatus.Open].includes(formStatus) &&
        selectedRfiType.wfType === WorkflowType.US) ||
      ([RfiStatus.Submitted, RfiStatus.OpenRev1, RfiStatus.OpenRev2].includes(formStatus) &&
        selectedRfiType.wfType === WorkflowType.EU);

    if (!rfiTypeWorkflowMatchFormStatus) {
      return;
    }

    const {
      locationDescription,
      reference,
      costImpact,
      scheduleImpact,
      priority,
      category,
      discipline,
      dueDateOffset,
      watchers,
      manager,
    } = selectedRfiType;

    const handlePopulatingAssignedTo = () => {
      switch (formStatus) {
        case RfiStatus.Open:
        case RfiStatus.OpenRev2: {
          fieldSetters.assignedTo(filterValidAssignees(selectedRfiType.projectReviewer));
          break;
        }
        case RfiStatus.OpenRev1: {
          fieldSetters.assignedTo(filterValidAssignees(selectedRfiType.projectCoordinator));
          break;
        }
        case RfiStatus.Submitted: {
          // AssignedTo - Manager
          if (formStatus === RfiStatus.Submitted) {
            fieldSetters.assignedTo(filterValidAssignees(manager) || []);
          }
          break;
        }
        default:
      }
    };

    // AssignedTo
    handlePopulatingAssignedTo();

    // locationDetails
    if (fieldSetters.locationDescription) {
      fieldSetters.locationDescription(locationDescription || '');
    }
    // reference (External ID )
    if (fieldSetters.reference) {
      fieldSetters.reference(reference || '');
    }
    // costImpact
    if (fieldSetters.costImpact) {
      fieldSetters.costImpact(costImpact || '');
    }
    // scheduleImpact
    if (fieldSetters.scheduleImpact) {
      fieldSetters.scheduleImpact(scheduleImpact || '');
    }
    // priority
    if (fieldSetters.priority) {
      fieldSetters.priority(priority || RfiPriority.Normal);
    }
    // category
    if (fieldSetters.category) {
      fieldSetters.category(category || []);
    }
    // discipline
    if (fieldSetters.discipline) {
      fieldSetters.discipline(discipline || []);
    }
    // dueDateOffset
    if (fieldSetters.dueDateOffset) {
      if (dueDateOffset) {
        fieldSetters.dueDateOffset(incrementTodayByDays(dueDateOffset));
      } else {
        fieldSetters.dueDateOffset(null);
      }
    }
    // watchers
    if (fieldSetters.watchers) {
      fieldSetters.watchers(filterValidAssignees(watchers));
    }
  }, [
    selectedRfiTypeId,
    formStatus,
    isFetchingRfiTypes,
    disabled,
    fieldSetters,
    selectedRfiType,
    oxygen.loading,
  ]);
};
